import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  accrualRelevant,
  applySimulation,
  forecastPending,
  resolveEntry,
  rollupDays,
  splitMoney,
  uncertainPrepayment,
  windowOpeningBalance,
  type Basis,
  type CashFlowEntry as CoreEntry,
  type Granularity,
  type SimulationDelta,
} from '@progear/finance-core';
import { cn } from '@/lib/utils';
import { useFreshnessRecords } from '@/hooks/useFreshness';
import { useKmdActuals, useSaveKmdActual } from '@/hooks/useKmdActuals';
import {
  useCashFlow,
  useCreateCashFlowEntry,
  useCreateCashFlowSchedule,
  useDeleteCashFlowEntry,
  useResolveSettlement,
  useMarkCashFlowEntryPaid,
  useSplitCashFlowEntry,
  useUpdateCashFlowEntry,
} from '@/hooks/useCashFlow';
import {
  formatEtDate,
  formatEur,
  yearRange,
  type CashFlowRange,
  type CashFlowRequestOptions,
  type CreateEntrySeed,
  type DayRollup,
  type MoneyMode,
  type ResolveSettlementIntent,
} from '@/lib/cashFlow';
import {
  buildFacets,
  emptyFilters,
  entryMatchesFilters,
  facetCounts,
  filterCount,
  type FilterKey,
  type TodayMode,
} from '@/lib/cashFlowFilters';
import { SegmentedControl } from './SegmentedControl';
import { CashFlowTable } from './CashFlowTable';
import { CalendarGrid, SORT_OPTIONS, type DaySort } from './CalendarGrid';
import { KpiStrip } from './KpiStrip';
import { CreateEntryDialog } from './CreateEntryDialog';
import { Ledger, type LedgerScope } from './Ledger';
import { EnrichedEntryDetail } from './enriched/EnrichedEntryDetail';
import {
  CashFlowWorkspaceProvider,
  useCashFlowWorkspace,
  type ActiveMatch,
  type CashFlowViewKind,
  type ForecastScope,
  type PrepaymentScope,
} from './CashFlowWorkspace';
import { Composition } from './Composition';
import { SourceFreshness } from './SourceFreshness';
import { FilterChipBar, FilterPanel } from './CashFlowFilter';
import { PeriodPicker } from './PeriodPicker';
import { ShellActionsContext } from '@/components/shell/AppShell';
import { useSheetsExport } from '@/hooks/useSheetsExport';

/**
 * Module 1.1 — the cash-flow calendar (primary view). Orchestrates the shared
 * query layer (useCashFlow), the global NET/GROSS + granularity toggles, the
 * faceted filter, the local "what-if" simulation overlay, the per-source
 * freshness row and the period navigation. Every number it shows comes from
 * finance-core (`rollupDays`/`applySimulation`) — the view selects and filters,
 * it never computes money (replit.md L9 + master architectural rule).
 */
const MODE_OPTIONS: { value: MoneyMode; label: string; activeClass: string }[] = [
  { value: 'net', label: 'Neto', activeClass: 'bg-[#2dd4bf]/15 text-[#2dd4bf]' },
  { value: 'gross', label: 'Bruto', activeClass: 'bg-[hsl(var(--vatmode))]/20 text-[hsl(var(--vatmode))]' },
];

/** Rahavoog / Tekkepõhine — the shared reporting-basis toggle (finance-core `Basis`).
 *  Like Neto/Bruto it lives in the header chrome and switches EVERY lens at once
 *  (L14): cash = the treasury calendar (settlement dates, carry-forward, balance);
 *  accrual = each document on its issue date, reconciling with an accrual report. */
const BASIS_OPTIONS: { value: Basis; label: string; activeClass: string }[] = [
  { value: 'cashBasis', label: 'Rahavoog', activeClass: 'bg-[hsl(var(--income))]/15 text-[hsl(var(--income))]' },
  { value: 'accrualBasis', label: 'Tekkepõhine', activeClass: 'bg-[hsl(var(--pending))]/15 text-[hsl(var(--pending))]' },
];

/** Kõik / Kindlad — the shared prepayment scope of the CASH lens (workspace
 *  `prepaymentScope`). "Kindlad" hides katteta ettemaksud — unpaid income
 *  prepayment invoices with no final invoice or kreeditarve yet (finance-core
 *  `uncertainPrepayment`) — from EVERY consumer at once (days, lines, register,
 *  KPI, drill; L14). DISABLED (grayed, not hidden — the header must not jump)
 *  while the accrual basis is on: that lens already drops ALL prepayments, so
 *  the scope is inert there. */
const PREPAYMENT_OPTIONS: { value: PrepaymentScope; label: string; activeClass: string }[] = [
  { value: 'all', label: 'Kõik', activeClass: 'bg-[hsl(var(--income))]/15 text-[hsl(var(--income))]' },
  { value: 'certain', label: 'Kindlad', activeClass: 'bg-[hsl(var(--vatmode))]/20 text-[hsl(var(--vatmode))]' },
];

/** Prognoos: Väljas / Koos / Lahus — the shared plan-money scope (workspace
 *  `forecastScope`, L14). "Väljas" drops still-pending forecast documents from
 *  EVERY consumer via finance-core's `forecastPending` (a settled forecast is
 *  real money and always stays); "Koos" blends plan into one number; "Lahus"
 *  (default) keeps the blend AND breaks the plan share out as the amber
 *  "sh prognoos" sub-lines + the confirmed balance twin. */
const FORECAST_SCOPE_OPTIONS: { value: ForecastScope; label: string; activeClass: string }[] = [
  { value: 'excluded', label: 'Väljas', activeClass: 'bg-[hsl(var(--cost))]/15 text-[hsl(var(--cost))]' },
  { value: 'blended', label: 'Koos', activeClass: 'bg-[hsl(var(--income))]/15 text-[hsl(var(--income))]' },
  { value: 'split', label: 'Lahus', activeClass: 'bg-[hsl(var(--pending))]/15 text-[hsl(var(--pending))]' },
];

const GRANULARITY_OPTIONS: { value: Granularity; label: string }[] = [
  { value: 'day', label: 'Päev' },
  { value: 'week', label: 'Nädal' },
  { value: 'month', label: 'Kuu' },
  { value: 'quarter', label: 'Kvartal' },
];

const VIEW_OPTIONS: { value: CashFlowViewKind; label: string }[] = [
  { value: 'table', label: 'Tabel' },
  { value: 'calendar', label: 'Kalender' },
];

/** An external filter request (e.g. Tarnijate radar "Ava rahavoos") — App bridges the
 *  party name into the SHARED workspace free-text search, so every lens narrows to the
 *  supplier (L14). A fresh nonce re-fires the same supplier on repeat clicks. */
export interface CashFlowFilterRequest {
  search: string;
  nonce: number;
}

export interface CashFlowViewProps {
  range?: CashFlowRange;
  options?: CashFlowRequestOptions;
  /** An external "locate" request (Cmd+K result / `?entry=` deep-link) — App bridges
   *  it into the shared workspace so the CURRENT lens reveals the document (L14). */
  locateRequest?: ActiveMatch | null;
  /** An external filter request — see {@link CashFlowFilterRequest}. */
  filterRequest?: CashFlowFilterRequest | null;
}

const OVERDUE_SCOPE: LedgerScope = {
  label: 'Üle tähtaja',
  match: (row) => row.status === 'overdue',
};

interface CashFlowViewBodyProps {
  options?: CashFlowRequestOptions;
  locateRequest?: ActiveMatch | null;
  filterRequest?: CashFlowFilterRequest | null;
}

function CashFlowViewBody({ options, locateRequest = null, filterRequest = null }: CashFlowViewBodyProps) {
  // Every shared lens capability — money-mode, granularity, lens, register scope,
  // faceted filter, calendar day-sort, the period window/year/label and the "Täna"
  // re-centre nonce — comes from the ONE workspace state, so the calendar, the table
  // and the register move together (replit.md L14: lenses share every function).
  const {
    mode,
    setMode,
    basis,
    setBasis,
    prepaymentScope,
    setPrepaymentScope,
    forecastEmphasis,
    setForecastEmphasis,
    forecastScope,
    setForecastScope,
    granularity,
    setGranularity,
    view,
    setView,
    ledgerScope,
    setLedgerScope,
    filters,
    setFilters,
    daySort,
    setDaySort,
    todayNonce,
    bumpToday,
    windowRange,
    setWindowRange,
    year,
    setYear,
    setPeriodText,
    selectedIdSet,
    selectedOnly,
    activeMatch,
    locateEntry,
    selectedEntryDetailId,
    openEntryDetail,
    closeEntryDetail,
  } = useCashFlowWorkspace();

  // Bridge an EXTERNAL locate (Cmd+K result / `?entry=` deep-link, passed as a prop)
  // into the shared workspace exactly once per request, so the CURRENT lens reveals the
  // document via the same useLocateScroll path the register's "Leia" uses (L14). The
  // nonce guards against re-firing on unrelated re-renders.
  const seenLocateNonce = useRef<number | null>(null);
  useEffect(() => {
    if (!locateRequest || seenLocateNonce.current === locateRequest.nonce) return;
    seenLocateNonce.current = locateRequest.nonce;
    locateEntry(locateRequest.entryId);
  }, [locateRequest, locateEntry]);

  // Bridge an EXTERNAL filter request (Tarnijate radar "Ava rahavoos") into the shared
  // workspace free-text search once per nonce — the same field the register search box
  // drives, so the calendar, table and register all narrow to the supplier (L14).
  const seenFilterNonce = useRef<number | null>(null);
  useEffect(() => {
    if (!filterRequest || seenFilterNonce.current === filterRequest.nonce) return;
    seenFilterNonce.current = filterRequest.nonce;
    setFilters((f) => ({ ...f, search: filterRequest.search }));
  }, [filterRequest, setFilters]);

  const [showComposition, setShowComposition] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [todayMenuOpen, setTodayMenuOpen] = useState(false);

  // The ONE shared rich create dialog (L14) — opened from every lens (calendar day "+",
  // table line "+", register toolbar "+"), the edge FAB and any future surface. The body
  // owns the seed + the create mutation; no lens owns create state. `null` = closed.
  const [createSeed, setCreateSeed] = useState<CreateEntrySeed | null>(null);

  // Local "what-if" simulation overlay (scenario === 'sim'); never persisted.
  const [scenario, setScenario] = useState<'base' | 'sim'>('base');
  const [simDeltas, setSimDeltas] = useState<SimulationDelta[]>([]);
  const [scenarioMenuOpen, setScenarioMenuOpen] = useState(false);

  const sheetsExport = useSheetsExport(year, options);
  // Guard against double-click: disabled while the mutation is in-flight.
  const sheetsExportFired = useRef(false);

  const { data, isLoading } = useCashFlow(windowRange, options);
  const { records } = useFreshnessRecords(options);
  const create = useCreateCashFlowEntry(options);
  const createSchedule = useCreateCashFlowSchedule(options);
  const del = useDeleteCashFlowEntry(options);
  const update = useUpdateCashFlowEntry(options);
  const split = useSplitCashFlowEntry(options);
  const resolve = useResolveSettlement(options);
  // Manual mark-paid — the register's dispatch of the ONE shared mark-paid function
  // (L14; the detail's Arveldus dispatches the same mutation). The key is minted per
  // confirm so a React-Query retry re-applies the SAME settlement (at-least-once safe).
  const markPaid = useMarkCashFlowEntryPaid(options);
  // KMD backcast (1.12) — the recorded EMTA declarations + the mark mutation,
  // both through the shared query layer (master rule; L14 shared state).
  const { actuals: kmdActuals } = useKmdActuals(options);
  const saveKmdActual = useSaveKmdActual(options);

  const baseEntries = data?.entries ?? [];
  const coreBase = baseEntries as unknown as CoreEntry[];

  const facets = useMemo(() => buildFacets(baseEntries), [baseEntries]);
  const facetToday = data?.today ?? '';
  const anchor = data?.anchor ?? null;
  // Newest observed bank balance (LHV import / manual) — informational only; the
  // balance line anchors on the OPENING anchor above, never on this figure.
  const latestBalance = data?.latestBalance ?? null;
  const counts = useMemo(() => {
    const out: Partial<Record<FilterKey, Record<string, number>>> = {};
    for (const facet of facets) out[facet.key] = facetCounts(baseEntries, facet.key, facetToday);
    return out;
  }, [facets, baseEntries, facetToday]);

  const simActive = scenario === 'sim' && simDeltas.length > 0;
  const filtersActive = filterCount(filters) > 0;
  // "Ainult valitud" — the register's cross-lens focus. When on (and something is
  // ticked) EVERY lens narrows to the selected documents from this ONE source, so the
  // calendar, table and register stay identical (replit.md L14); the selected set
  // itself lives in the workspace, never in a lens.
  const selectionActive = selectedOnly && selectedIdSet.size > 0;
  // The accrual lens re-resolves EVERY placement client-side: the API's days are
  // cash-basis, so whenever the basis differs the body re-rolls through the same
  // finance-core path the overlays use (one truth per number — never a second
  // server variant of the rollup).
  const accrualActive = basis === 'accrualBasis';
  // "Ettemaksud: Kindlad" — only meaningful on the cash lens (accrual already drops
  // every prepayment via accrualRelevant, so the scope is inert there by design).
  const prepaymentScopeActive = prepaymentScope === 'certain' && !accrualActive;
  // "Prognoos: Väljas" — drop still-pending forecast documents from EVERY consumer.
  // Settled forecasts are real money (finance-core `forecastPending` is false for
  // them), so they always stay; the other two scopes keep the full blend.
  const forecastExcluded = forecastScope === 'excluded';
  const overlayActive =
    simActive ||
    filtersActive ||
    selectionActive ||
    accrualActive ||
    prepaymentScopeActive ||
    forecastExcluded;

  // "Ainult maksmata" is a SHARED shortcut over the settlementStatus facet (not a
  // calendar-only hide) so it narrows BOTH lenses identically (L14). "On" = the four
  // non-settled statuses are EXACTLY the selection (an exact match keeps aria-pressed
  // honest — manually adding `settled` must not leave the shortcut reading "on");
  // toggling off clears the facet entirely.
  const UNPAID_STATUSES = ['overdue', 'partial', 'planned', 'pending'] as const;
  const onlyUnpaidOn =
    filters.sel.settlementStatus.size === UNPAID_STATUSES.length &&
    UNPAID_STATUSES.every((s) => filters.sel.settlementStatus.has(s));
  const toggleOnlyUnpaid = () =>
    setFilters((f) => ({
      ...f,
      sel: { ...f.sel, settlementStatus: new Set<string>(onlyUnpaidOn ? [] : [...UNPAID_STATUSES]) },
    }));

  // The "Täna" quick filter is the SHARED todayMode (see cashFlowFilters): writing it
  // narrows EVERY lens identically through the same finance-core re-rollup (L14) — it is
  // never a lens-local "today" toggle. DUE keeps rows whose due date is today, ISSUED
  // (koostatud) keeps rows whose document/issue date is today; `none` clears it.
  const setTodayMode = (m: TodayMode) => setFilters((f) => ({ ...f, todayMode: m }));

  // Numbers flow through finance-core: overlay (what-if) → filter → re-rollup.
  const simCore = simActive ? applySimulation(coreBase, simDeltas) : coreBase;
  const filteredCore = filtersActive
    ? simCore.filter((entry) =>
        entryMatchesFilters(
          entry as unknown as Parameters<typeof entryMatchesFilters>[0],
          filters,
          facetToday,
          basis,
        ),
      )
    : simCore;
  // The selection focus narrows whatever the sim + filter already produced (one
  // source for all three lenses — L14).
  const selectedCore = selectionActive
    ? filteredCore.filter((entry) => selectedIdSet.has(entry.id))
    : filteredCore;
  // The accrual lens drops prepayment invoices ONCE here (finance-core's
  // `accrualRelevant` — a prepayment is a cash event, never accrual revenue), so
  // EVERY consumer below (days, lines, register, KPI, drill) reads the same
  // relevant set and the lenses cannot diverge (L14).
  // The cash lens' "Kindlad" scope drops katteta ettemaksud the same ONE place
  // (finance-core's `uncertainPrepayment` — an unpaid, unlinked income prepayment
  // is pure hope, not forecastable cash).
  const scopedCore = accrualActive
    ? selectedCore.filter((entry) => accrualRelevant(entry as unknown as { docType?: string | null }))
    : prepaymentScopeActive
      ? selectedCore.filter(
          (entry) => !uncertainPrepayment(entry as unknown as Parameters<typeof uncertainPrepayment>[0]),
        )
      : selectedCore;
  // "Prognoos: Väljas" drops still-pending plan documents ONCE here (finance-core's
  // `forecastPending` — a settled forecast is real money and survives), so EVERY
  // consumer below (days, lines, register, KPI, drill) reads the same set (L14).
  const effectiveCore = forecastExcluded
    ? scopedCore.filter((entry) => !forecastPending(entry, facetToday, basis))
    : scopedCore;
  const effectiveEntries = effectiveCore as unknown as typeof baseEntries;
  const effectiveDays: DayRollup[] =
    data && overlayActive
      ? (rollupDays(effectiveCore, {
          today: data.today,
          openingBalance: data.openingBalance,
          // Pin the simulated balance at the same day the server pinned it (the
          // OPENING anchor's as_of_date; with no anchor the server pins Jan 1 of
          // the current year at 0 €) — otherwise the overlay re-anchors elsewhere
          // and the simulated balances diverge from the base ones.
          anchorDate: data.anchor?.asOfDate ?? `${data.today.slice(0, 4)}-01-01`,
          from: data.from,
          to: data.to,
          basis,
        }) as unknown as DayRollup[])
      : (data?.days ?? []);

  // Display "Algsaldo" = balance at the START of the visible window, derived in
  // finance-core from the first rolled-up day (one truth). The response's
  // `openingBalance` is pinned at the ANCHOR's as_of_date — with a past anchor
  // and a narrower window it is NOT the window start, so it is only ever fed
  // back into `rollupDays` (above), never shown directly.
  const windowOpening = windowOpeningBalance(
    effectiveDays as unknown as Parameters<typeof windowOpeningBalance>[0],
    data?.openingBalance ?? 0,
  );

  // Overdue (carried-forward, still pending) count — surfaced inline in the title
  // row and reused by the body register chip. Computed from the same finance-core
  // resolution the calendar/table use; the component never sums money itself.
  // Under the accrual basis nothing is ever carried, so the affordance disappears
  // structurally (overdue is a cash concept).
  const overdueCount = data
    ? effectiveEntries.filter((e) => resolveEntry(e as CoreEntry, data.today, basis).carriedForward).length
    : 0;

  const setSimulation = (active: boolean) => {
    setScenario(active ? 'sim' : 'base');
    if (!active) setSimDeltas([]);
  };

  const handleReschedule = (id: string, expectedDate: string) => {
    if (scenario === 'sim') {
      setSimDeltas((deltas) => [
        ...deltas.filter((d) => !(d.type === 'reschedule' && d.id === id)),
        { type: 'reschedule', id, expectedDate },
      ]);
    } else {
      update.mutate({ id, patch: { expectedDate } });
    }
  };

  // Split an entry: carve `moveGrossCents` (GROSS) off onto `targetDate`, leaving the
  // remainder as the origin ghost. In a simulation it becomes a local split delta
  // (the remainder stays on the original due day); in the base scenario it persists
  // through the split endpoint. All cents come from finance-core (`splitMoney`).
  const handleSplit = (id: string, targetDate: string, moveGrossCents: number) => {
    if (scenario === 'sim') {
      // The id always originates from a rendered chip, so it is present in the core.
      const target = effectiveCore.find((e) => e.id === id)!;
      const { moved, remainder } = splitMoney(target.money, moveGrossCents);
      const origin = (target as unknown as { originExpectedDate: string | null }).originExpectedDate ?? target.expectedDate;
      setSimDeltas((deltas) => [
        ...deltas,
        {
          type: 'split',
          id,
          parts: [
            { idSuffix: 'jaak', expectedDate: origin, money: remainder },
            { idSuffix: 'osa', expectedDate: targetDate, money: moved },
          ],
        },
      ]);
    } else {
      split.mutate({ id, input: { targetDate, moveGrossCents } });
    }
  };

  // Resolve an outstanding gap (kanna maha / faktooring). Unlike split/reschedule this
  // is a real settlement decision, never a what-if — so it always persists (like
  // mark-paid / comment / priority), regardless of the scenario. The orchestrator mints
  // the idempotency key once per click so a React-Query retry re-applies the SAME write
  // at-least-once safely; every closed amount is computed server-side (L9).
  const handleResolve = (id: string, intent: ResolveSettlementIntent) => {
    resolve.mutate({ id, input: { ...intent, idempotencyKey: crypto.randomUUID() } });
  };

  // Every create affordance funnels through ONE opener: each lens hands the slice it
  // knows (calendar a date, table a line's kind/category, register/FAB just today), the
  // body fills the rest and renders the single shared dialog (L14). The date defaults to
  // the data anchor's "today" so an unseeded open still lands on a sensible day.
  const openCreateEntry = (seed: CreateEntrySeed = {}) =>
    setCreateSeed({ date: seed.date ?? data?.today, kind: seed.kind, category: seed.category });
  const closeCreateEntry = () => setCreateSeed(null);

  // Hard-delete a manual entry — the document-level destructive action shared by the
  // register row + the enriched detail (reachable from every lens via drill-to-register,
  // L14). The server is the authoritative manual-only guard (409 otherwise); the UI only
  // offers it for `source === 'manual'`. The audit trigger records the DELETE.
  const handleDelete = (id: string) => del.mutate(id);

  const shellActionsEl = useContext(ShellActionsContext);

  // The scenario switch portals onto the shell search line so the module chrome stays
  // minimal (with no shell provider — isolated tests — it renders inline in the header).
  // Creating an entry is no longer a header button: it moved to the edge "+" FAB and the
  // per-lens "+" affordances, all opening the ONE shared dialog (L14).
  const primaryActions = (
    <div className="relative flex items-center gap-2">
      {/* Google Sheets export button */}
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          disabled={sheetsExport.isPending}
          onClick={() => {
            /* v8 ignore next */
            if (sheetsExport.isPending) return;
            sheetsExportFired.current = false;
            sheetsExport.reset();
            sheetsExport.mutate();
          }}
          className="rounded-md border border-border bg-card px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-accent disabled:cursor-wait disabled:opacity-60"
          title={`Ekspordi ${year}. aasta rahavoog Google Spreadsheetiks`}
        >
          {sheetsExport.isPending ? 'Ekspordib…' : 'Ekspordi Sheets'}
        </button>
        {sheetsExport.data?.url && !sheetsExport.isPending && (
          <a
            href={sheetsExport.data.url}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-md border border-border bg-card px-2.5 py-1.5 text-xs font-medium text-[hsl(var(--income))] hover:bg-accent"
          >
            Ava tabel ↗
          </a>
        )}
        {sheetsExport.error && !sheetsExport.isPending && (
          <span className="text-xs text-[hsl(var(--cost))]" title={sheetsExport.error.message}>
            Viga eksportimisel
          </span>
        )}
      </div>
      <button
        type="button"
        aria-expanded={scenarioMenuOpen}
        onClick={() => setScenarioMenuOpen((o) => !o)}
        className="rounded-md border border-border bg-card px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-accent"
      >
        Stsenaarium: {scenario === 'sim' ? 'Simulatsioon' : 'Baas'}
      </button>
      {scenarioMenuOpen && (
        <>
          <div className="fixed inset-0 z-40" aria-hidden onClick={() => setScenarioMenuOpen(false)} />
          <div role="menu" aria-label="Stsenaarium" className="absolute right-0 top-full z-50 mt-1 w-44 rounded-md border border-border bg-card p-1 shadow-xl">
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setSimulation(false);
                setScenarioMenuOpen(false);
              }}
              className="block w-full rounded px-2 py-1 text-left text-xs text-foreground hover:bg-accent"
            >
              Baas
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setSimulation(true);
                setScenarioMenuOpen(false);
              }}
              className="block w-full rounded px-2 py-1 text-left text-xs text-foreground hover:bg-accent"
            >
              Simulatsioon
            </button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <section className="flex h-full flex-col gap-2 p-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-base font-semibold tracking-tight">Rahavoog</h1>
          <PeriodPicker />
          <div className="relative">
            <button
              type="button"
              aria-label="Ainult täna kiirfilter"
              aria-haspopup="menu"
              aria-expanded={todayMenuOpen}
              aria-pressed={filters.todayMode !== 'none'}
              onClick={() => setTodayMenuOpen((o) => !o)}
              title="Näita ainult tänaseid dokumente — kitsendab KÕIKI vaateid (kalender · tabel · register)"
              className={
                filters.todayMode !== 'none'
                  ? 'rounded-md border border-primary/50 bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary'
                  : 'rounded-md border border-border px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground'
              }
            >
              Ainult täna
              {filters.todayMode === 'due'
                ? ': tähtaeg'
                : filters.todayMode === 'document'
                  ? ': koostatud'
                  : ''}
            </button>
            {todayMenuOpen && (
              <>
                <div className="fixed inset-0 z-40" aria-hidden onClick={() => setTodayMenuOpen(false)} />
                <div
                  role="menu"
                  aria-label="Täna — millised dokumendid"
                  className="absolute left-0 top-full z-50 mt-1 w-48 rounded-md border border-border bg-card p-1 shadow-xl"
                >
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={filters.todayMode === 'due'}
                    onClick={() => {
                      setTodayMode('due');
                      setTodayMenuOpen(false);
                    }}
                    className="block w-full rounded px-2 py-1 text-left text-xs text-foreground hover:bg-accent"
                  >
                    Tähtaeg täna
                  </button>
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={filters.todayMode === 'document'}
                    onClick={() => {
                      setTodayMode('document');
                      setTodayMenuOpen(false);
                    }}
                    className="block w-full rounded px-2 py-1 text-left text-xs text-foreground hover:bg-accent"
                  >
                    Koostatud täna
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setTodayMode('none');
                      setTodayMenuOpen(false);
                    }}
                    className="mt-1 block w-full rounded px-2 py-1 text-left text-xs text-muted-foreground hover:bg-accent"
                  >
                    Eemalda
                  </button>
                </div>
              </>
            )}
          </div>
          {data && (
            <button
              type="button"
              title="Tänasesse — lähtesta vaade jooksvale aastale ja keri tänasele"
              onClick={() => {
                const todayYear = Number(data.today.slice(0, 4));
                setYear(todayYear);
                setWindowRange(yearRange(todayYear));
                setGranularity('month');
                setPeriodText(null);
                bumpToday();
              }}
              className="rounded px-1.5 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              Täna <span className="tabular-nums text-foreground/80">{formatEtDate(data.today)}</span>
            </button>
          )}
          {data && overdueCount > 0 && (
            <button
              type="button"
              onClick={() => setLedgerScope(OVERDUE_SCOPE)}
              title="Ava üle tähtaja kirjed"
              className="rounded-md border border-[hsl(var(--cost))]/50 bg-[hsl(var(--cost))]/10 px-2 py-0.5 text-xs font-medium text-[hsl(var(--cost))] transition-colors hover:bg-[hsl(var(--cost))]/20"
            >
              {overdueCount} üle tähtaja
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SourceFreshness records={records} />
          {/* Bank figures are CASH truths — under the accrual basis they are
              meaningless next to document-dated sums, so the chrome hides them
              instead of showing numbers that cannot reconcile. */}
          {/* No opening anchor yet → the balance line starts from 0 € at year
              start; a quiet cue points the human at Seaded → Pangasaldo ankur
              (a hint, not a number — the chrome never invents a balance). */}
          {data && !anchor && !accrualActive && (
            <span
              title="Algsaldot pole sisestatud — saldojoon algab 0 €-st aasta algusest. Sisesta aasta alguse kontojääk: Seaded → Pangasaldo ankur."
              className="inline-flex items-center gap-1.5 rounded-md border border-[hsl(var(--cost))]/40 bg-[hsl(var(--cost))]/10 px-2 py-0.5 text-[11px] text-[hsl(var(--cost))]"
            >
              <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-[hsl(var(--cost))]" />
              Algsaldo puudub
            </span>
          )}
          {/* The newest OBSERVED balance (LHV import / manual entry) — pure info.
              The balance line itself rolls forward from the opening anchor; while
              payment coverage is partial the two figures legitimately differ. */}
          {latestBalance && !accrualActive && (
            <span
              title={`Viimane teadaolev pangasaldo (${latestBalance.source === 'lhv' ? 'LHV väljavõttest' : 'käsitsi sisestatud'}, seisuga ${formatEtDate(latestBalance.asOfDate)}) — ainult info; saldojoon arvutatakse algsaldost edasi`}
              className="inline-flex items-center gap-1.5 rounded-md border border-[hsl(var(--income))]/30 bg-[hsl(var(--income))]/10 px-2 py-0.5 text-[11px] text-[hsl(var(--income))]"
            >
              <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-[hsl(var(--income))]" />
              Pangajääk
              <span className="tabular-nums font-semibold text-foreground">{formatEur(latestBalance.balanceCents)}</span>
            </span>
          )}
          {shellActionsEl ? createPortal(primaryActions, shellActionsEl) : primaryActions}
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <SegmentedControl options={GRANULARITY_OPTIONS} value={granularity} onChange={setGranularity} ariaLabel="Periood" />
        <SegmentedControl options={VIEW_OPTIONS} value={view} onChange={setView} ariaLabel="Vaade" />
        <button
          type="button"
          aria-expanded={filterOpen}
          aria-controls="cashflow-filter-panel"
          onClick={() => setFilterOpen((o) => !o)}
          className={
            filtersActive || filterOpen
              ? 'inline-flex items-center gap-1.5 rounded-md border border-primary/50 bg-primary/10 px-2.5 py-1.5 text-xs text-foreground'
              : 'inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs text-foreground hover:bg-accent'
          }
        >
          <span aria-hidden className="text-[10px]">
            ▾
          </span>{' '}
          Rohkem filtreid
          {filtersActive && (
            <span className="rounded-full bg-primary/20 px-1.5 text-[10px] font-semibold text-primary">{filterCount(filters)}</span>
          )}
        </button>
        <button
          type="button"
          aria-pressed={onlyUnpaidOn}
          onClick={toggleOnlyUnpaid}
          title="Näita ainult maksmata kirjeid — kitsendab MÕLEMAT vaadet"
          className={
            onlyUnpaidOn
              ? 'rounded-md border border-primary/50 bg-primary/10 px-2.5 py-1.5 text-xs font-medium text-primary'
              : 'rounded-md border border-border px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-accent'
          }
        >
          {onlyUnpaidOn ? '● ' : '○ '}Ainult maksmata
        </button>
        {view === 'calendar' && (
          <label className="flex items-center gap-1 text-[11px] text-muted-foreground">
            <span>Päeva järjestus</span>
            <select
              aria-label="Järjesta päevasiseseid kirjeid"
              value={daySort}
              onChange={(ev) => setDaySort(ev.target.value as DaySort)}
              className="rounded-md border border-border bg-card px-1.5 py-1 text-[11px] text-foreground"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        )}
        <button
          type="button"
          aria-pressed={scenario === 'sim'}
          onClick={() => setSimulation(scenario !== 'sim')}
          className={
            scenario === 'sim'
              ? 'rounded-md border border-[hsl(var(--pending))]/50 bg-[hsl(var(--pending))]/10 px-2.5 py-1.5 text-xs font-medium text-[hsl(var(--pending))]'
              : 'rounded-md border border-border px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-accent'
          }
        >
          Simuleeri muudatus
        </button>
        <button
          type="button"
          onClick={() => setLedgerScope(null)}
          className="rounded-md border border-border bg-background px-2.5 py-1.5 text-xs text-foreground transition-colors hover:border-primary/60 hover:text-primary"
        >
          Kõik dokumendid
        </button>
        {/* Koosseis (saldo composition) decomposes the running BANK balance — a
            cash truth — so the opener hides under the accrual basis, like every
            other saldo affordance (same rule as the table's saldo rows). */}
        {basis !== 'accrualBasis' && (
          <button
            type="button"
            onClick={() => setShowComposition(true)}
            className="rounded-md border border-border bg-background px-2.5 py-1.5 text-xs text-foreground transition-colors hover:border-primary/60 hover:text-primary"
          >
            Koosseis
          </button>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setForecastEmphasis((v) => !v)}
            aria-pressed={forecastEmphasis}
            title="Rõhuta prognoosi kirjeid kõigis vaadetes"
            className={cn(
              'rounded-md border px-2 py-1 text-[11px] transition-colors',
              forecastEmphasis
                ? 'border-[hsl(var(--pending))]/60 bg-[hsl(var(--pending))]/15 text-[hsl(var(--pending))]'
                : 'border-border text-muted-foreground hover:bg-accent/40',
            )}
          >
            Rõhuta prognoos
          </button>
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Prognoos</span>
          <SegmentedControl
            options={FORECAST_SCOPE_OPTIONS}
            value={forecastScope}
            onChange={setForecastScope}
            ariaLabel="Prognoosi ulatus"
          />
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Alus</span>
          <SegmentedControl options={BASIS_OPTIONS} value={basis} onChange={setBasis} ariaLabel="Arvestuse alus" />
          <span
            className={cn(
              'text-[10px] uppercase tracking-wide text-muted-foreground',
              accrualActive && 'opacity-40',
            )}
          >
            Ettemaksud
          </span>
          <SegmentedControl
            options={PREPAYMENT_OPTIONS}
            value={prepaymentScope}
            onChange={setPrepaymentScope}
            ariaLabel="Ettemaksude ulatus"
            disabled={accrualActive}
            disabledTitle="Tekkepõhine alus jätab kõik ettemaksuarved nagunii välja — ulatus kehtib rahavoo alusel"
          />
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Summad</span>
          <SegmentedControl options={MODE_OPTIONS} value={mode} onChange={setMode} ariaLabel="Summa vaade" />
        </div>
      </div>

      {filterOpen && (
        <FilterPanel
          facets={facets}
          filters={filters}
          counts={counts}
          onToggle={(key, value) =>
            setFilters((f) => {
              const next = new Set(f.sel[key]);
              if (next.has(value)) next.delete(value);
              else next.add(value);
              return { ...f, sel: { ...f.sel, [key]: next } };
            })
          }
          onAmount={(which, value) =>
            setFilters((f) => ({ ...f, [which === 'min' ? 'amountMin' : 'amountMax']: value }))
          }
          onClear={() => setFilters(emptyFilters())}
          onClose={() => setFilterOpen(false)}
        />
      )}

      {(filtersActive || scenario === 'sim') && (
        <div className="flex flex-wrap items-center gap-2">
          <FilterChipBar
            facets={facets}
            filters={filters}
            onToggle={(key, value) =>
              setFilters((f) => {
                const next = new Set(f.sel[key]);
                next.delete(value);
                return { ...f, sel: { ...f.sel, [key]: next } };
              })
            }
            onAmount={(which, value) =>
              setFilters((f) => ({ ...f, [which === 'min' ? 'amountMin' : 'amountMax']: value }))
            }
            onClear={() => setFilters(emptyFilters())}
          />
          {scenario === 'sim' && (
            <span
              role="status"
              className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--pending))]/50 bg-[hsl(var(--pending))]/10 px-2.5 py-0.5 text-[11px] font-medium text-[hsl(var(--pending))]"
            >
              Simulatsioon aktiivne — {simDeltas.length} muudatust
              <button type="button" onClick={() => setSimulation(false)} className="hover:underline">
                Lähtesta
              </button>
            </span>
          )}
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Laen rahavoogu…</p>
      ) : !data ? (
        <p role="alert" className="text-sm text-red-300">
          Rahavoo laadimine ebaõnnestus.
        </p>
      ) : (
        <div className="relative flex min-h-0 flex-1 flex-col gap-4">
          <KpiStrip days={effectiveDays} entries={effectiveEntries} today={data.today} basis={basis} mode={mode} />
          {ledgerScope !== undefined && (
            <Ledger
              entries={effectiveEntries}
              today={data.today}
              scope={ledgerScope}
              onClose={() => setLedgerScope(undefined)}
              onReschedule={handleReschedule}
              onMarkPaid={(id, intent) =>
                markPaid.mutate({ id, input: { ...intent, idempotencyKey: crypto.randomUUID() } })
              }
              onSplit={handleSplit}
              onComment={(id, comment) => update.mutate({ id, patch: { comment } })}
              onPriority={(id, priority) => update.mutate({ id, patch: { priority } })}
              onResolve={handleResolve}
              onOpenEntry={openEntryDetail}
              onAddEntry={openCreateEntry}
              onDelete={handleDelete}
            />
          )}
          {showComposition && basis !== 'accrualBasis' && (
            <Composition
              days={effectiveDays}
              entries={effectiveEntries}
              today={data.today}
              openingBalance={windowOpening}
              onClose={() => setShowComposition(false)}
            />
          )}
          {view === 'calendar' ? (
            <div className="min-h-0 flex-1">
              <CalendarGrid
                days={effectiveDays}
                entries={effectiveEntries}
                mode={mode}
                forecastEmphasis={forecastEmphasis}
                today={data.today}
                granularity={granularity}
                openingBalance={windowOpening}
                anchor={data.anchor}
                onReschedule={handleReschedule}
                onSplit={handleSplit}
                onAddEntry={(date) => openCreateEntry({ date })}
                sortBy={daySort}
                centerNonce={todayNonce}
                onOpenScope={setLedgerScope}
                onOpenEntry={openEntryDetail}
                activeMatch={activeMatch}
                basis={basis}
                forecastScope={forecastScope}
              />
            </div>
          ) : (
            <div className="min-h-0 flex-1 overflow-auto">
              <CashFlowTable
                days={effectiveDays}
                entries={effectiveEntries}
                granularity={granularity}
                mode={mode}
                forecastEmphasis={forecastEmphasis}
                openingBalance={windowOpening}
                today={data.today}
                centerNonce={todayNonce}
                onOpenScope={setLedgerScope}
                onOpenEntry={openEntryDetail}
                onAddEntry={openCreateEntry}
                activeMatch={activeMatch}
                basis={basis}
                forecastScope={forecastScope}
                kmdActuals={kmdActuals}
                onSaveKmdActual={saveKmdActual.mutate}
              />
            </div>
          )}

          {selectedEntryDetailId !== null && (
            <EnrichedEntryDetail
              entryId={selectedEntryDetailId}
              today={data.today}
              mode={mode}
              requestOptions={options}
              onDelete={handleDelete}
              onClose={closeEntryDetail}
              onOpenEntry={openEntryDetail}
              candidatePrepayments={baseEntries}
              onOpenScope={(scope) => {
                // The declaration drill replaces the detail overlay with the
                // scoped register — the same scope the table lens's KMD row opens.
                closeEntryDetail();
                setLedgerScope(scope);
              }}
            />
          )}
        </div>
      )}

      {/* The edge "+" FAB — a persistent create affordance that never competes with the
          calendar grid for chrome (the primary-view rule: no button takes persistent space
          at the grid's expense). Portalled to document.body so a backdrop-filter/transform
          ancestor can't re-anchor its fixed position; z below the dialogs (z-50). It opens
          the ONE shared dialog seeded with today. */}
      {createPortal(
        <button
          type="button"
          aria-label="Lisa kirje"
          title="Lisa uus kirje"
          onClick={() => openCreateEntry()}
          className="fixed bottom-6 right-6 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-2xl font-light leading-none text-primary-foreground shadow-xl transition-colors hover:bg-primary/90"
        >
          <span aria-hidden>+</span>
        </button>,
        document.body,
      )}

      {/* The ONE shared rich create dialog — every lens "+" and the FAB open THIS exact
          component, so a manual entry is created identically from anywhere (L14). It owns
          no fetch/mutation: the body passes the seed + the shared create mutation. */}
      <CreateEntryDialog
        open={createSeed !== null}
        initialDate={createSeed?.date}
        initialKind={createSeed?.kind}
        initialCategory={createSeed?.category}
        onSubmit={(input) => {
          create.mutate(input);
          closeCreateEntry();
        }}
        onSubmitSchedule={(input) => {
          createSchedule.mutate(input);
          closeCreateEntry();
        }}
        onClose={closeCreateEntry}
      />
    </section>
  );
}

/**
 * The cash-flow primary view. Wraps the body in the shared workspace so the
 * calendar, the table and the universal register all read ONE state (L14).
 */
export function CashFlowView({
  range,
  options,
  locateRequest = null,
  filterRequest = null,
}: CashFlowViewProps) {
  return (
    <CashFlowWorkspaceProvider range={range}>
      <CashFlowViewBody options={options} locateRequest={locateRequest} filterRequest={filterRequest} />
    </CashFlowWorkspaceProvider>
  );
}
